import { supabase } from '../lib/supabase';

// Helper function to check if error is schema-related
function isSchemaError(error) {
  if (!error) return false;
  
  if (error?.code && typeof error?.code === 'string') {
    const errorClass = error?.code?.substring(0, 2);
    if (errorClass === '42' || errorClass === '08') {
      return true;
    }
    if (errorClass === '23') {
      return false;
    }
  }
  
  if (error?.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /function.*does not exist/i,
      /syntax error/i,
      /invalid.*syntax/i,
      /type.*does not exist/i,
      /undefined.*column/i,
      /undefined.*table/i,
      /undefined.*function/i,
    ];
    return schemaErrorPatterns?.some(pattern => pattern?.test(error?.message));
  }
  
  return false;
}

// Convert snake_case to camelCase
function toCamelCase(obj) {
  if (!obj) return obj;
  
  const camelObj = {};
  Object.keys(obj)?.forEach(key => {
    const camelKey = key?.replace(/_([a-z])/g, (_, letter) => letter?.toUpperCase());
    camelObj[camelKey] = obj?.[key];
  });
  return camelObj;
}

// Convert camelCase to snake_case
function toSnakeCase(obj) {
  if (!obj) return obj;
  
  const snakeObj = {};
  Object.keys(obj)?.forEach(key => {
    const snakeKey = key?.replace(/[A-Z]/g, letter => `_${letter?.toLowerCase()}`);
    snakeObj[snakeKey] = obj?.[key];
  });
  return snakeObj;
}

// Generate unique username
function generateUsername(firstName, lastName) {
  const firstPart = firstName?.toLowerCase()?.substring(0, 3);
  const lastPart = lastName?.toLowerCase()?.substring(0, 3);
  const randomNum = Math.floor(1000 + Math.random() * 9000);
  return `${firstPart}${lastPart}${randomNum}`;
}

// Generate secure password
function generatePassword() {
  // Simple 4-digit numeric password (0000-9999)
  const randomNum = Math.floor(Math.random() * 10000);
  // Pad with leading zeros to ensure 4 digits
  return randomNum?.toString()?.padStart(4, '0');
}

// Get all students for authenticated teacher
export async function getStudents(classroomId = null) {
  try {
    // Verify authentication
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    let query = supabase?.from('students')?.select('*')?.eq('teacher_id', user?.id);
    
    if (classroomId) {
      query = query?.eq('classroom_id', classroomId);
    }
    
    const { data, error } = await query?.order('created_at', { ascending: false });

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else if (error?.code === 'PGRST116') {
        return [];
      } else {
        console.log('Data error:', error?.message);
        return [];
      }
    }

    const students = data?.map(toCamelCase) || [];

    if (students?.length === 0) return students;

    // Fetch progress counts for all students in one query
    const studentIds = students?.map(s => s?.id);
    const { data: progressData } = await supabase
      ?.from('student_progress')
      ?.select('student_id, status')
      ?.in('student_id', studentIds);

    // Build a map: studentId -> { completed, inProgress }
    const progressMap = {};
    (progressData || [])?.forEach(row => {
      if (!progressMap?.[row?.student_id]) {
        progressMap[row.student_id] = { completed: 0, inProgress: 0 };
      }
      if (row?.status === 'completed') {
        progressMap[row.student_id].completed += 1;
      } else if (row?.status === 'in_progress') {
        progressMap[row.student_id].inProgress += 1;
      }
    });

    // Attach progress counts to each student
    return students?.map(s => ({
      ...s,
      journeysCompleted: progressMap?.[s?.id]?.completed || 0,
      journeysInProgress: progressMap?.[s?.id]?.inProgress || 0,
    }));
  } catch (error) {
    console.error('Error fetching students:', error);
    throw error;
  }
}

// Get single student by ID
export async function getStudentById(studentId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { data, error } = await supabase?.from('students')?.select('*')?.eq('id', studentId)?.eq('teacher_id', user?.id)?.maybeSingle();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        return null;
      }
    }

    return data ? toCamelCase(data) : null;
  } catch (error) {
    console.error('Error fetching student:', error);
    throw error;
  }
}

// Create new student with auto-generated credentials
export async function createStudent(studentData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Generate credentials
    const username = generateUsername(studentData?.firstName, studentData?.lastName);
    let password = generatePassword();

    // Prepare data for insertion
    const snakeData = {
      teacher_id: user?.id,
      classroom_id: studentData?.classroomId,
      username: username,
      password_hash: password, // Will be hashed by database trigger
      plain_password: password, // Store plain password for display
      full_name: `${studentData?.firstName} ${studentData?.lastName}`,
      email: studentData?.email || null,
      status: 'active'
    };

    const { data, error } = await supabase?.from('students')?.insert(snakeData)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    // Return student data with plain password for display
    return {
      ...toCamelCase(data),
      plainPassword: password // Include plain password for credential display
    };
  } catch (error) {
    console.error('Error creating student:', error);
    throw error;
  }
}

// Update student
export async function updateStudent(studentId, studentData) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const snakeData = toSnakeCase(studentData);
    delete snakeData?.id;
    delete snakeData?.teacher_id;
    delete snakeData?.created_at;
    delete snakeData?.username; // Username should not be updated
    delete snakeData?.password_hash; // Password hash should not be updated this way

    const { data, error } = await supabase?.from('students')?.update(snakeData)?.eq('id', studentId)?.eq('teacher_id', user?.id)?.select()?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    return toCamelCase(data);
  } catch (error) {
    console.error('Error updating student:', error);
    throw error;
  }
}

// Delete student
export async function deleteStudent(studentId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase?.from('students')?.delete()?.eq('id', studentId)?.eq('teacher_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    return true;
  } catch (error) {
    console.error('Error deleting student:', error);
    throw error;
  }
}

// Bulk delete students
export async function bulkDeleteStudents(studentIds) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    const { error } = await supabase?.from('students')?.delete()?.in('id', studentIds)?.eq('teacher_id', user?.id);

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    return true;
  } catch (error) {
    console.error('Error bulk deleting students:', error);
    throw error;
  }
}

// Reset student password
export async function resetPassword(studentId) {
  try {
    const { data: { user }, error: authError } = await supabase?.auth?.getUser();
    if (authError || !user) {
      throw new Error('User not authenticated');
    }

    // Generate new 4-digit password
    const newPassword = generatePassword();

    // Update both password_hash and plain_password
    const { data, error } = await supabase
      ?.from('students')
      ?.update({
        password_hash: newPassword, // Will be hashed by database trigger
        plain_password: newPassword // Store plain password for display
      })
      ?.eq('id', studentId)
      ?.eq('teacher_id', user?.id)
      ?.select()
      ?.single();

    if (error) {
      if (isSchemaError(error)) {
        console.error('Schema error:', error?.message);
        throw error;
      } else {
        console.log('Data error:', error?.message);
        throw new Error(error.message);
      }
    }

    // Return updated student data with new plain password
    return {
      ...toCamelCase(data),
      plainPassword: newPassword
    };
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
}